import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import QuickView from "@/components/QuickView";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";

const LOOKS = [
  {
    id: "look-01",
    acte: "Acte I",
    title: "Persévérance",
    location: "Studio Blanc — Paris",
    image: "/products/orig/mld-001.webp",
    hotspots: [{ x: 50, y: 32, productId: "mld-001" }],
  },
  {
    id: "look-02",
    acte: "Acte II",
    title: "Épreuve Noire",
    location: "Studio Blanc — Paris",
    image: "/products/orig/mld-002.webp",
    hotspots: [
      { x: 50, y: 30, productId: "mld-002" },
      { x: 50, y: 68, productId: "mld-007" },
    ],
  },
  {
    id: "look-03",
    acte: "Acte III",
    title: "Trêve",
    location: "Studio Blanc — Paris",
    image: "/products/orig/mld-003.webp",
    hotspots: [
      { x: 50, y: 34, productId: "mld-003" },
      { x: 50, y: 68, productId: "mld-007" },
    ],
  },
];

export default function Lookbook() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [quickView, setQuickView] = useState(null);

  useEffect(() => {
    api.get("/products").then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  const findProduct = (id) => products.find((p) => p.id === id);

  return (
    <div className="mx-auto max-w-[1700px] px-5 pb-28 pt-36 sm:px-8 lg:px-16 lg:pt-44" data-testid="lookbook-page">
      <Reveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-ember">Campaign — Automne</p>
        <h1 className="mt-4 font-blackletter text-6xl leading-[1] sm:text-8xl lg:text-9xl">
          Trois Actes
        </h1>
        <p className="mt-6 max-w-lg font-serif text-xl italic text-zinc-400">
          Trois pièces, un studio blanc. Touchez les marqueurs pour acheter chaque look.
        </p>
      </Reveal>

      <div className="mt-20 space-y-24 lg:space-y-36">
        {LOOKS.map((look, idx) => (
          <Reveal key={look.id} delay={0.05}>
            <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">{look.acte}</span>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-4xl">
                  {look.title}
                </h2>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{look.location}</p>
            </div>

            <div
              className={`relative overflow-hidden border border-white/5 ${
                idx % 2 === 1 ? "lg:ml-[16%]" : "lg:mr-[16%]"
              }`}
              data-testid={`look-${look.id}`}
            >
              <div className="bg-[#f4f1ec]">
                <img
                  src={look.image}
                  alt={`${look.title} — ${look.location}`}
                  loading="lazy"
                  className="mx-auto h-[70vh] w-auto max-w-full object-contain lg:h-[85vh]"
                />
              </div>

              {look.hotspots.map((h, i) => {
                const product = findProduct(h.productId);
                const key = `${look.id}-${i}`;
                const isActive = active === key;
                return (
                  <div key={key} className="absolute" style={{ left: `${h.x}%`, top: `${h.y}%` }}>
                    <button
                      data-testid={`hotspot-${look.id}-${i}`}
                      onClick={() => setActive(isActive ? null : key)}
                      className="relative flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                      aria-label={`Hotspot ${i + 1}`}
                    >
                      <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full border border-ink/40" />
                      <span className="flex h-6 w-6 items-center justify-center rounded-full border border-ink/25 bg-white text-ink shadow-sm">
                        <Plus className={`h-3.5 w-3.5 transition-transform duration-300 ${isActive ? "rotate-45" : ""}`} />
                      </span>
                    </button>
                    <AnimatePresence>
                      {isActive && product && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.97 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          data-testid={`hotspot-card-${look.id}-${i}`}
                          className="absolute left-4 top-4 z-10 w-56 border border-white/10 bg-ink/90 p-4 backdrop-blur-xl"
                        >
                          <div className="flex gap-3">
                            <img
                              src={`${product.image}&w=200&auto=format`}
                              alt=""
                              className="h-16 w-14 shrink-0 object-cover"
                            />
                            <div>
                              <p className="font-display text-xs font-semibold uppercase leading-tight">
                                {product.name}
                              </p>
                              <p className="mt-1 font-mono text-[10px] text-zinc-400">
                                €{product.price.toFixed(0)}
                              </p>
                            </div>
                          </div>
                          <button
                            data-testid={`hotspot-view-${look.id}-${i}`}
                            onClick={() => setQuickView(product)}
                            className="mt-3 flex w-full items-center justify-center gap-2 border border-ember py-2.5 font-mono text-[9px] uppercase tracking-[0.25em] text-ember transition-colors duration-300 hover:bg-ember hover:text-white"
                          >
                            View piece <ArrowUpRight className="h-3 w-3" />
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {quickView && <QuickView product={quickView} onClose={() => setQuickView(null)} />}
      </AnimatePresence>
    </div>
  );
}
